import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { Textarea } from "@bednbody/ui/components/textarea";
import { GIFTPASS_BATCH_MAX } from "@bednbody/types";
import { useState } from "react";
import { toast } from "sonner";
import { parseCardNumbers } from "./giftpass.import";
import { useImportPrintedGiftpasses } from "./giftpass.hooks";

/**
 * Printed cards become working GiftPasses by their card numbers (0376, G9).
 * Superadmin only — the page shows the button to Superadmin, the server refuses
 * anyone else.
 */
export function ImportPrintedDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (batchId: string) => void;
}) {
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const importCards = useImportPrintedGiftpasses();
  const { codes, invalid } = parseCardNumbers(text);
  const ready =
    label.trim() !== "" &&
    codes.length > 0 &&
    codes.length <= GIFTPASS_BATCH_MAX &&
    invalid.length === 0;

  const submit = () =>
    importCards.mutate(
      { label: label.trim(), codes },
      {
        onSuccess: (result) => {
          toast.success(`${result.count} printed cards imported`);
          setLabel("");
          setText("");
          onOpenChange(false);
          onImported(result.batchId);
        },
        onError: (error) => toast.error(error.message),
      },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import printed cards</DialogTitle>
          <DialogDescription>
            Paste the card numbers from the promo tracker. Each becomes a working ₱299 GiftPass,
            used with the number printed on the card. Logged with your name.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor="import-label">Name</FieldLabel>
          <Input
            id="import-label"
            type="text"
            maxLength={120}
            placeholder="e.g. 2026 Aug batch — test 50"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </Field>
        <Field data-invalid={invalid.length ? true : undefined}>
          <FieldLabel htmlFor="import-codes">Card numbers</FieldLabel>
          <Textarea
            id="import-codes"
            rows={8}
            className="font-mono"
            placeholder={"0824000100082026D\n0824000200082026D"}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <FieldDescription>
            {invalid.length ? (
              <span className="text-destructive">
                {invalid.length} line{invalid.length === 1 ? " is" : "s are"} not a card number, for
                example {invalid[0]}.
              </span>
            ) : codes.length > GIFTPASS_BATCH_MAX ? (
              <span className="text-destructive">Import at most 1,000 at a time.</span>
            ) : (
              `${codes.length} card number${codes.length === 1 ? "" : "s"} — one per line, or separated by commas.`
            )}
          </FieldDescription>
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Back
          </Button>
          <Button disabled={!ready || importCards.isPending} onClick={submit}>
            Import {codes.length || ""} cards
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
